import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { CheckCircle, XCircle, Loader2, ShoppingBag, ArrowRight } from "lucide-react";
import { API } from "../apiConfig";


const PayPalSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);

  const orderId = searchParams.get("order_id");
  const paypalToken = searchParams.get("token"); // PayPal returns this

  useEffect(() => {
    if (paypalToken) {
      capturePayment();
    } else {
      setStatus("error");
      setError("Invalid payment session");
    }
  }, [paypalToken]);

  const capturePayment = async () => {
    try {
      // First, get the PayPal order ID from our order
      const orderRes = await axios.get(`${API}/orders/${orderId}`);
      const paypalOrderId = orderRes.data?.metadata?.paypal_order_id || paypalToken;

      // Capture the payment
      const response = await axios.post(`${API}/checkout/paypal/capture`, {
        paypal_order_id: paypalOrderId
      });

      if (response.data.success) {
        setStatus("success");
        setOrderDetails({
          orderId: orderId,
          amount: response.data.amount,
          email: response.data.payer_email
        });
      } else {
        setStatus("error");
        setError("Payment capture failed");
      }
    } catch (err) {
      console.error("PayPal capture error:", err);
      setStatus("error");
      setError(err.response?.data?.detail || "Payment processing failed");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  return (
    <div className="min-h-screen" data-testid="paypal-success-page">
      <Navigation />

      <div className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-xl mx-auto text-center">
          {status === "processing" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
              </div>
              <h1 className="text-3xl font-medium">Processing Payment</h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your PayPal payment...
              </p>
            </motion.div>
          )}

          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-medium">Payment Successful!</h1>
              <p className="text-muted-foreground">
                Thank you for your purchase. Your order has been confirmed.
              </p>

              {orderDetails && (
                <div className="bg-card rounded-2xl p-6 border border-border/50 text-left space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Order ID</span>
                    <span className="font-mono">{orderDetails.orderId?.slice(-8)}</span>
                  </div>
                  {orderDetails.amount && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount Paid</span>
                      <span className="font-medium">{formatPrice(orderDetails.amount)}</span>
                    </div>
                  )}
                  {orderDetails.email && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Confirmation sent to</span>
                      <span>{orderDetails.email}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Link to="/orders">
                  <Button variant="outline" className="rounded-full px-6">
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    View Orders
                  </Button>
                </Link>
                <Link to="/products">
                  <Button className="rounded-full px-6">
                    Continue Shopping
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}

          {status === "error" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="text-3xl font-medium">Payment Issue</h1>
              <p className="text-muted-foreground">
                {error || "There was an issue processing your payment."}
              </p>
              <p className="text-sm text-muted-foreground">
                Don't worry - if money was deducted, it will be refunded automatically.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Link to="/cart">
                  <Button variant="outline" className="rounded-full px-6">
                    Return to Cart
                  </Button>
                </Link>
                <Link to="/">
                  <Button className="rounded-full px-6">
                    Go Home
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PayPalSuccessPage;
